import { Injectable } from '@angular/core';
import { FormRepository } from '@ieCore/repositories/form.repository';
import {
  requestLoading,
  RequestLoadingType,
} from '@ieCore/helpers/request-loading.helper';
import {
  ActivityFormResponseDto,
  FormResponseDto,
} from 'shared-general-libs/dto/form';
import { FrameworkTags } from 'shared-general-libs/enum/framework';
import {
  PhoneCodeResponseDto,
  RegionResponseDto,
} from 'shared-general-libs/dto/region';
import { ReportTemplate } from 'shared-general-libs/type/report';
import { OrganizationService } from '@ieCore/services/organization.service';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormDefaultValueTypes } from 'shared-general-libs/enum/form';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  constructor(
    private formRepository: FormRepository,
    private organizationService: OrganizationService,
  ) {}

  /**
   * Item 6: merges the caller org's `activityFormDefaults[activity]` into
   * the served form config as `defaultValue: { type: CONST, value }` per
   * field key — WITHOUT overriding a field that already carries a
   * defaultValue from form-service. Returns the SAME `formConfig` object
   * reference (byte-identical) in EVERY case where nothing actually
   * changes: an absent/empty org config, a config whose keys match no
   * field, or a config whose only matching field(s) already carry a
   * defaultValue — not just the absent-config case. An org-fetch failure
   * also resolves to `{}` at the OrganizationService layer, so it falls
   * into the same untouched path.
   *
   * Single choke point: every caller of getFormByActivityName (ie-manage-
   * activity, accounting-consolidated-details, activity-partner-tree) is
   * covered automatically.
   */
  private static mergeActivityFormDefaults(
    formConfig: ActivityFormResponseDto,
    defaults: Record<string, string> | undefined,
  ): ActivityFormResponseDto {
    if (!defaults || !Object.keys(defaults).length) {
      return formConfig;
    }

    let changed = false;
    const mergedForm = formConfig.form.map((field) => {
      const value = defaults[field.key];
      if (value === undefined || field.defaultValue) {
        return field;
      }
      changed = true;
      return {
        ...field,
        defaultValue: { type: FormDefaultValueTypes.CONST, value },
      };
    });

    if (!changed) {
      return formConfig;
    }

    return { ...formConfig, form: mergedForm };
  }

  getFormByName(
    name: string,
    payload: unknown = {},
  ): RequestLoadingType<FormResponseDto[]> {
    return requestLoading(this.formRepository.getFormByName(name, payload));
  }

  getReportTemplate(id: string): RequestLoadingType<ReportTemplate> {
    return requestLoading(this.formRepository.getReportTemplate(id));
  }

  getFormByActivityName(
    activity: string,
    frameworkTags?: FrameworkTags[],
  ): RequestLoadingType<ActivityFormResponseDto> {
    return requestLoading(
      combineLatest([
        this.formRepository.getFormByActivityName(activity, frameworkTags),
        this.organizationService.getActivityFormDefaults(),
      ]).pipe(
        map(([formConfig, activityFormDefaults]) =>
          FormService.mergeActivityFormDefaults(
            formConfig,
            activityFormDefaults[activity],
          ),
        ),
      ),
    );
  }

  getRegions(lang: string): RequestLoadingType<RegionResponseDto[]> {
    return requestLoading(this.formRepository.getRegions(lang));
  }

  getPhoneCodes(): RequestLoadingType<PhoneCodeResponseDto[]> {
    return requestLoading(this.formRepository.getPhoneCodes());
  }
}
